'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { getApiBaseUrl } from '@/lib/config'
import MarketRatesStrip, {
  MarketRatesStripSkeleton,
} from '@/components/market/MarketRatesStrip'
import { foldCityKey } from '@/lib/weather-city'

type WeatherSnapshot = {
  city: string
  temperature: number | null
  condition: string | null
  icon: string | null
  fetchedAt?: string
  source?: string
}

type WeatherCityOption = {
  city: string
  temperature: number | null
  condition: string | null
  icon: string | null
  selected: boolean
}

const BAR_SHELL =
  'flex min-h-[3.25rem] flex-row items-center gap-2 rounded-[5px] bg-gray-50 px-2 py-2.5 dark:bg-slate-900 sm:min-h-[3.5rem] sm:gap-3 sm:px-3 sm:py-3'

function formatTemp(t: number | null | undefined) {
  if (typeof t !== 'number' || !Number.isFinite(t)) return '--'
  return String(Math.round(t))
}

function normalizeTrLower(s: string) {
  return s.trim().toLocaleLowerCase('tr-TR')
}

/** Strict Mode / remount — aynı anda tek cities isteği */
let citiesInflight: Promise<WeatherCityOption[]> | null = null
let citiesCached: WeatherCityOption[] | null = null

function fetchWeatherCities(apiBase: string): Promise<WeatherCityOption[]> {
  if (citiesCached) return Promise.resolve(citiesCached)
  if (citiesInflight) return citiesInflight
  citiesInflight = fetch(`${apiBase}/api/weather/cities`, { cache: 'no-store' })
    .then((res) => res.json())
    .then((json) => {
      const list = Array.isArray(json?.data) ? (json.data as WeatherCityOption[]) : []
      citiesCached = list
      return list
    })
    .catch(() => [] as WeatherCityOption[])
    .finally(() => {
      citiesInflight = null
    })
  return citiesInflight
}

const latestInflight = new Map<string, Promise<WeatherSnapshot | null>>()

function fetchWeatherLatest(apiBase: string, city: string): Promise<WeatherSnapshot | null> {
  const key = city || 'İstanbul'
  const existing = latestInflight.get(key)
  if (existing) return existing
  const p = fetch(
    `${apiBase}/api/weather/latest?city=${encodeURIComponent(key)}`,
    { cache: 'no-store' },
  )
    .then((res) => res.json())
    .then((json) => (json?.data as WeatherSnapshot | null) ?? null)
    .catch(() => null)
    .finally(() => {
      window.setTimeout(() => latestInflight.delete(key), 1500)
    })
  latestInflight.set(key, p)
  return p
}

function WeatherOnlySkeleton() {
  return (
    <div className="flex shrink-0 items-center gap-1.5 sm:gap-2.5" aria-hidden="true">
      <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-gray-200 sm:h-7 sm:w-7 dark:bg-slate-700" />
      <div className="min-w-0 space-y-1.5">
        <div className="h-4 w-14 animate-pulse rounded bg-gray-200 sm:h-[1.125rem] sm:w-16 dark:bg-slate-700" />
        <div className="h-3 w-16 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="h-3.5 w-3.5 shrink-0 animate-pulse rounded-sm bg-gray-200 dark:bg-slate-700" />
    </div>
  )
}

export default function WeatherMarketBar() {
  const apiBase = useMemo(() => getApiBaseUrl(), [])

  const [weather, setWeather] = useState<WeatherSnapshot | null>(null)
  const [cities, setCities] = useState<WeatherCityOption[]>([])
  const [city, setCity] = useState<string>('İstanbul')
  const [cityReady, setCityReady] = useState(false)
  const [hasManualCity, setHasManualCity] = useState(false)
  const [locationCity, setLocationCity] = useState<string | null>(null)
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [useLocationWeather, setUseLocationWeather] = useState(false)
  const [cityPickerOpen, setCityPickerOpen] = useState(false)
  const [cityQuery, setCityQuery] = useState('')
  const [weatherLoading, setWeatherLoading] = useState(true)
  const weatherBusy = weatherLoading

  // localStorage şehri okunmadan İstanbul isteği atılmasın
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('hm_weather_city')?.trim()
      if (saved) setCity(saved)
    } catch {
      /* ignore */
    }
    setCityReady(true)
  }, [])

  useEffect(() => {
    if (!cityReady) return
    let cancelled = false
    void fetchWeatherCities(apiBase).then((list) => {
      if (cancelled) return
      setCities(list)
      if (!list.length) return
      setCity((current) => {
        if (list.some((c) => c.city === current)) return current
        return list.find((c) => c.selected)?.city ?? list[0].city
      })
    })
    return () => {
      cancelled = true
    }
  }, [apiBase, cityReady])

  useEffect(() => {
    if (!cityReady) return
    let cancelled = false
    async function loadCity() {
      try {
        setWeatherLoading(true)
        const data = await fetchWeatherLatest(apiBase, city || 'İstanbul')
        if (cancelled) return
        setWeather(data)
      } catch {
        if (cancelled) return
        setWeather(null)
      } finally {
        if (!cancelled) setWeatherLoading(false)
      }
    }
    async function loadByCoords(lat: number, lon: number) {
      try {
        setWeatherLoading(true)
        const wRes = await fetch(
          `${apiBase}/api/weather/latest-by-coords?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}&city=${encodeURIComponent(locationCity || '')}`,
          { cache: 'no-store' },
        )
        const wJson = await wRes.json()
        if (cancelled) return
        setWeather(wJson?.data ?? null)
      } catch {
        if (cancelled) return
        setWeather(null)
      } finally {
        if (!cancelled) setWeatherLoading(false)
      }
    }
    const tick = () => {
      if (useLocationWeather && locationCoords) return loadByCoords(locationCoords.lat, locationCoords.lon)
      return loadCity()
    }
    void tick()
    const id = window.setInterval(() => void tick(), 5 * 60 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [apiBase, city, cityReady, useLocationWeather, locationCoords, locationCity])

  const useMyLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude
        const lon = pos.coords.longitude
        try {
          setLocationCoords({ lat, lon })
          const res = await fetch(
            `${apiBase}/api/weather/resolve-city?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}`,
            { cache: 'no-store' },
          )
          const json = await res.json()
          const resolved = typeof json?.city === 'string' ? json.city : null
          if (!resolved) return
          setLocationCity(resolved)
          setUseLocationWeather(true)
          const normalized = normalizeTrLower(resolved)
          const match =
            cities.find((c) => normalizeTrLower(c.city) === normalized) ??
            cities.find((c) => normalized.includes(normalizeTrLower(c.city)))
          if (match) {
            setCity(match.city)
            setUseLocationWeather(true)
          }
        } catch {
          /* ignore */
        }
      },
      () => {
        /* ignore */
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 },
    )
  }

  useEffect(() => {
    try {
      if (hasManualCity) window.localStorage.setItem('hm_weather_city', city)
    } catch {
      /* ignore */
    }
  }, [city, hasManualCity])

  const filteredCities = cityQuery
    ? cities.filter((c) => foldCityKey(c.city).includes(foldCityKey(cityQuery)))
    : cities

  const showSkeleton = !cityReady || (weatherLoading && weather === null)

  return (
    <div className="relative mb-4 weather-market-bar">
      {cityPickerOpen && (
        <div className="absolute left-2 right-2 top-14 z-50 max-h-[min(60dvh,20rem)] overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-slate-950 sm:left-4 sm:right-auto sm:top-16 sm:w-64 sm:max-w-[calc(100vw-2rem)]">
          <div className="border-b border-gray-100 p-2 dark:border-slate-800">
            <input
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              placeholder="Şehir ara…"
              className="w-full rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-500 dark:bg-slate-900 dark:text-gray-100"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-auto">
            {locationCity && (
              <button
                type="button"
                onClick={() => {
                  setUseLocationWeather(true)
                  setCityPickerOpen(false)
                  setCityQuery('')
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-900 ${useLocationWeather ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{locationCity}</span>
                  <span className="text-xs text-red-500">GPS</span>
                </div>
              </button>
            )}
            {(filteredCities.length ? filteredCities : cities).slice(0, 100).map((c) => (
              <button
                key={c.city}
                type="button"
                onClick={() => {
                  setHasManualCity(true)
                  setCity(c.city)
                  setUseLocationWeather(false)
                  try {
                    window.localStorage.setItem('hm_weather_city', c.city)
                  } catch {
                    /* ignore */
                  }
                  setCityPickerOpen(false)
                  setCityQuery('')
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-900 ${normalizeTrLower(c.city) === normalizeTrLower(city) && !useLocationWeather ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-900 dark:text-gray-100">{c.city}</span>
                  {typeof c.temperature === 'number' && (
                    <span className="text-xs tabular-nums text-gray-400">{Math.round(c.temperature)}°</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={BAR_SHELL}
        role={showSkeleton ? 'status' : undefined}
        aria-busy={showSkeleton || undefined}
        aria-label={showSkeleton ? 'Piyasa ve hava durumu yükleniyor' : undefined}
      >
        {showSkeleton ? (
          <>
            <WeatherOnlySkeleton />
            <div className="h-8 w-px shrink-0 bg-gray-300 sm:h-10 dark:bg-slate-700" />
            <MarketRatesStripSkeleton count={6} />
          </>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center sm:h-7 sm:w-7">
                {!weatherBusy && weather?.icon ? (
                  <Image
                    src={`/weather-icons/${weather.icon}`}
                    alt={weather?.condition || 'Hava durumu'}
                    width={26}
                    height={26}
                    className="h-6 w-6 drop-shadow sm:h-7 sm:w-auto"
                  />
                ) : null}
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-black tabular-nums text-gray-900 sm:text-lg dark:text-gray-100">
                    {weatherBusy ? '--' : formatTemp(weather?.temperature)}°C
                  </span>
                  {!weatherBusy && weather?.condition && (
                    <span className="hidden text-[11px] text-gray-500 sm:inline dark:text-gray-400">
                      {weather.condition}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCityPickerOpen((v) => !v)
                    setCityQuery('')
                  }}
                  className="max-w-full truncate text-left text-[11px] text-gray-700 hover:text-red-800 sm:max-w-none sm:text-xs dark:text-gray-300 dark:hover:text-red-300"
                >
                  {useLocationWeather && locationCity ? locationCity : city || 'Şehir'} ▾
                </button>
              </div>
              <button
                type="button"
                onClick={useMyLocation}
                className="shrink-0 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                title="Konumumu kullan"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                  <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
                </svg>
              </button>
            </div>

            <div className="h-8 w-px shrink-0 bg-gray-300 sm:h-10 dark:bg-slate-700" />

            <MarketRatesStrip preset="home" scrollable linked />
          </>
        )}
      </div>
    </div>
  )
}
